/**
 * Work Item Type helpers
 *
 * Work item types are seeded by the backend into the `workitemtypes` RxDB
 * collection with backend-generated GUID ids (NOT stable string slugs). The
 * default system type is the one with `isSystem: true` and name "Task".
 *
 * Never hardcode a type id — always resolve it from the synced collection,
 * otherwise the id won't match the backend GUID and the sync push will be
 * rejected (the backend deserializes `workItemTypeId` as a nullable Guid).
 */

/** Minimal shape needed to resolve a default type. */
export interface WorkItemTypeLike {
  id: string
  name: string
  isSystem: boolean
}

/**
 * Resolves the default work item type id from a project's available types.
 * Prefers the system "Task" type, then any system type, then the first
 * available type. Returns null when no types are available.
 */
export function resolveDefaultWorkItemTypeId(
  types: ReadonlyArray<WorkItemTypeLike>,
): string | null {
  if (types.length === 0) return null
  const systemTask = types.find(
    (t) => t.isSystem && t.name.trim().toLowerCase() === 'task',
  )
  if (systemTask) return systemTask.id
  const firstSystem = types.find((t) => t.isSystem)
  return (firstSystem ?? types[0])?.id ?? null
}
